import React from 'react';
import {Options} from 'react-select';
import {SelectedCountryCodeContainer, StyledSearchingSelect} from './styled';
import {LocationType, SelectOption, isNumeric} from '@chekin/core';
import {useFetchLocations} from '../../../../hooks';
import {SelectRefType} from '@guestapp/old-ui-kit';
import {customCountriesFilter} from '@chekin/shared';

export type CountryCodeOption = {
  name: string;
  code: string;
};

export type PhoneCodesSelectProps = {
  value?: CountryCodeOption | null;
  onChange: (option: CountryCodeOption) => void;
  defaultCode?: string;
  readOnly?: boolean;
  disabled?: boolean;
  error?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

type PhoneCodeOption = SelectOption<LocationType | undefined>;

const getPhoneOption = (option: PhoneCodeOption) => {
  if (option.data && !isNumeric(option?.value)) {
    const phoneCodeValue = `+${option.data.phone_code}`;

    return {
      label: `${option.label} ${phoneCodeValue}`,
      value: phoneCodeValue,
    };
  } else {
    return {label: option.label, value: option.value};
  }
};

const getPhoneOptionLabel = (option: Parameters<typeof getPhoneOption>[0]) => {
  const phoneOption = getPhoneOption(option);
  return phoneOption.label;
};

const checkIfOptionSelected = (
  option: PhoneCodeOption,
  selectValue: Options<PhoneCodeOption>,
) => {
  const selectedValue = selectValue?.[0];

  return option?.label === selectedValue?.label && option?.value === selectedValue?.value;
};

const PhoneCodesSelect = React.forwardRef<
  SelectRefType<LocationType | undefined>,
  PhoneCodesSelectProps
>(
  (
    {
      onChange,
      disabled,
      value,
      readOnly,
      error,
      searchValue: externalSearchValue,
      onSearchChange: externalOnSearchChange,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const {phoneCodesAsOptions} = useFetchLocations();

    const [internalSearchValue, setInternalSearchValue] = React.useState('');
    const searchValue = externalSearchValue ?? internalSearchValue;
    const setSearchValue = externalOnSearchChange ?? setInternalSearchValue;

    const transformedValue = React.useMemo(() => {
      if (!value?.code) {
        return;
      }

      return {label: value?.name, value: value?.code};
    }, [value?.code, value?.name]);

    const handleCountryCodeChange = React.useCallback(
      (option: PhoneCodeOption | null) => {
        if (!option) return;
        const phoneOption = getPhoneOption(option);
        onChange({name: phoneOption.label, code: phoneOption.value});
        setSearchValue('');
      },
      [onChange, setSearchValue],
    );

    return (
      <StyledSearchingSelect<LocationType | undefined>
        ref={ref}
        options={phoneCodesAsOptions}
        formatOptionLabel={getPhoneOptionLabel}
        isOptionSelected={checkIfOptionSelected}
        filterOption={customCountriesFilter}
        onChange={handleCountryCodeChange}
        value={transformedValue}
        disabled={disabled}
        className="phone-field__code-select"
        inputValue={searchValue}
        onInputChange={newValue => setSearchValue(newValue)}
        onFocus={onFocus}
        onBlur={onBlur}
        trigger={(_, trigger) => (
          <SelectedCountryCodeContainer
            onClick={() => !disabled && !readOnly && trigger()}
            disabled={disabled}
            readOnly={readOnly}
            $isEmpty={!value?.code}
            $error={error}
            type="button"
            className="code-select kiosko:text-kiosko-s"
          >
            {value?.code ?? '+00'}
          </SelectedCountryCodeContainer>
        )}
      />
    );
  },
);

export {PhoneCodesSelect};
